import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import avatarAlex from "../assets/images/avatar-alex.jpeg";
import avatarJordan from "../assets/images/avatar-jordan.jpeg";
import avatarMaya from "../assets/images/avatar-maya.jpeg";
import avatarSara from "../assets/images/avatar-sara.jpeg";
import heroDashboard from "../assets/images/hero-dashboard.jpeg";
import { motion } from "framer-motion";
import {
  Bell,
  Plus,
  UsersRound,
  BookOpenText,
  CalendarCheck,
  MessageSquare,
} from "lucide-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function DashboardPage() {
  const performanceData = {
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    datasets: [
      {
        label: "Avg. Grade",
        data: [85, 88, 90, 87, 92, 91, 93],
        fill: true,
        backgroundColor: "rgba(59, 122, 231, 0.2)", // matches --color-primary
        borderColor: "#3B7AF6",
        tension: 0.4,
      },
    ],
  };

  const performanceOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { mode: "index", intersect: false },
    },
    scales: { y: { beginAtZero: true, max: 100 } },
  };

  const completionData = {
    labels: ["Week 1", "Week 2", "Week 3", "Week 4"],
    datasets: [
      {
        label: "Completed",
        data: [75, 80, 85, 90],
        backgroundColor: "#3B7AF6",
      },
    ],
  };

  const completionOptions = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, max: 100 } },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <>
      <section className="relative mb-8 overflow-hidden rounded-2xl">
        <div
          className="h-56 w-full bg-cover bg-center md:h-72"
          style={{ backgroundImage: `url(${heroDashboard})` }}
        />
        <div
          className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/40 to-white \
            dark:from-gray-800/60 dark:via-gray-800/40 dark:to-gray-800"
        />
        <div className="relative z-10 p-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-50">
            Good afternoon, Educator!
          </h1>
          <p className="mt-1 text-gray-700 dark:text-gray-300">
            Here's an overview of your impact today.
          </p>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Photo by{' '}
            <a
              href="https://www.pexels.com/@goumbik"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Lukas
            </a>{' '}
            on{' '}
            <a
              href="https://www.pexels.com"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Pexels
            </a>
          </p>
        </div>
      </section>
      <motion.div
      className="space-y-8"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
        <div className="flex justify-end items-center space-x-4">
        <button className="relative">
          <Bell className="w-6 h-6 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100" />
          <span className="absolute -top-1 -right-1 text-[10px] bg-primary text-white rounded-full px-1">
            3
          </span>
        </button>
        <button className="inline-flex items-center bg-primary text-white px-4 py-2 rounded-lg shadow hover:bg-primary-dark transition">
          <Plus className="w-4 h-4 mr-2" /> New Course
        </button>
      </div>

      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        variants={itemVariants}
      >
        <motion.div variants={itemVariants} className="card p-6">
          <div className="flex items-center">
            <UsersRound className="w-6 h-6 text-primary" />
            <h3 className="ml-3 text-sm font-medium text-gray-500 dark:text-gray-300">
              Students Enrolled
            </h3>
          </div>
          <p className="mt-4 text-2xl font-semibold">128</p>
          <p className="mt-1 text-gray-400 dark:text-gray-500">Across 4 courses</p>
        </motion.div>
        <motion.div variants={itemVariants} className="card p-6">
          <div className="flex items-center">
            <BookOpenText className="w-6 h-6 text-primary" />
            <h3 className="ml-3 text-sm font-medium text-gray-500 dark:text-gray-300">
              Active Courses
            </h3>
          </div>
          <p className="mt-4 text-2xl font-semibold">8</p>
          <p className="mt-1 text-gray-400 dark:text-gray-500">This semester</p>
        </motion.div>
        <motion.div variants={itemVariants} className="card p-6">
          <div className="flex items-center">
            <CalendarCheck className="w-6 h-6 text-primary" />
            <h3 className="ml-3 text-sm font-medium text-gray-500 dark:text-gray-300">
              Pending Assignments
            </h3>
          </div>
          <p className="mt-4 text-2xl font-semibold">14</p>
          <p className="mt-1 text-gray-400 dark:text-gray-500">Needs grading</p>
        </motion.div>
        <motion.div variants={itemVariants} className="card p-6">
          <div className="flex items-center">
            <MessageSquare className="w-6 h-6 text-primary" />
            <h3 className="ml-3 text-sm font-medium text-gray-500 dark:text-gray-300">
              Messages
            </h3>
          </div>
          <p className="mt-4 text-2xl font-semibold">5</p>
          <p className="mt-1 text-gray-400 dark:text-gray-500">Unread</p>
        </motion.div>
      </motion.div>

      <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-6" variants={itemVariants}>
        <motion.div variants={itemVariants} className="bg-white shadow-lg rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Performance Trend</h2>
          <Line data={performanceData} options={performanceOptions} />
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white shadow-lg rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">
            Assignment Completion
          </h2>
          <Bar data={completionData} options={completionOptions} />
        </motion.div>
      </motion.div>

      <motion.div variants={itemVariants} className="card p-6">
        <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
        <div className="space-y-4">
          <motion.div variants={itemVariants} className="flex items-center space-x-4">
            <div className="avatar">
              <div className="w-12 rounded-full">
                <img src={avatarAlex} alt="Alex Nguyen" />
              </div>
            </div>
            <div>
              <p className="font-medium">Alex Nguyen</p>
              <p className="text-sm text-gray-600">
                Submitted 'Math Homework 5'
              </p>
            </div>
            <div className="ml-auto text-xs text-gray-500">10m ago</div>
          </motion.div>

          <motion.div variants={itemVariants} className="flex items-center space-x-4">
            <div className="avatar">
              <div className="w-12 rounded-full">
                <img src={avatarJordan} alt="Jordan Lee" />
              </div>
            </div>
            <div>
              <p className="font-medium">Jordan Lee</p>
              <p className="text-sm text-gray-600">
                Commented on 'History Essay'
              </p>
            </div>
            <div className="ml-auto text-xs text-gray-500">30m ago</div>
          </motion.div>

          <motion.div variants={itemVariants} className="flex items-center space-x-4">
            <div className="avatar">
              <div className="w-12 rounded-full">
                <img src={avatarMaya} alt="Maya Patel" />
              </div>
            </div>
            <div>
              <p className="font-medium">Maya Patel</p>
              <p className="text-sm text-gray-600">
                Asked a question in 'Chemistry 101'
              </p>
            </div>
            <div className="ml-auto text-xs text-gray-500">1h ago</div>
          </motion.div>

          <motion.div variants={itemVariants} className="flex items-center space-x-4">
            <div className="avatar">
              <div className="w-12 rounded-full">
                <img src={avatarSara} alt="Sara Kim" />
              </div>
            </div>
            <div>
              <p className="font-medium">Sara Kim</p>
              <p className="text-sm text-gray-600">
                Joined 'Biology Lab'
              </p>
            </div>
            <div className="ml-auto text-xs text-gray-500">2h ago</div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
    </>
  );
}
